/**
 * Procurement page model — pure functions (no React) so the numbers on the page are testable:
 * per-LPO delivery summary and lateness, the KPI strip, site-request urgency and the filters.
 */
import { ageDays, formatNumber, toNumber } from '@/lib/format';

export const NONE = '__none__';

const num = (value) => toNumber(value) ?? 0;
const text = (value) => String(value ?? '').trim();
const lower = (value) => text(value).toLowerCase();

const CLOSED_STATUSES = new Set(['cancelled', 'closed', 'completed']);

/** "Carrier Gulf, Belimo UAE +2 more" — a KPI line that never cuts a supplier in half (the full list goes in a tooltip). */
export function supplierSummary(names, shown = 2) {
  const list = (Array.isArray(names) ? names : []).filter(Boolean);
  if (list.length <= shown + 1) return list.join(', ');
  return `${list.slice(0, shown).join(', ')} +${formatNumber(list.length - shown)} more`;
}

/**
 * One LPO with what the page needs: quantities, value still to come, the delivery state
 * (Not delivered / Partially delivered / Delivered / Cancelled — never the stored "Active") and lateness.
 * `daysLate` > 0 only while something is still to be delivered after the expected date.
 */
export function summarizeLpo(lpo, { now } = {}) {
  const lines = Array.isArray(lpo?.line_items) ? lpo.line_items : [];
  let ordered = 0;
  let delivered = 0;
  let remaining = 0;
  let linesValue = 0;
  let remainingValue = 0;
  let openLines = 0;
  lines.forEach((line) => {
    const lineOrdered = num(line.ordered_qty);
    const lineDelivered = num(line.delivered_qty);
    const lineRemaining = line.remaining_qty !== undefined && line.remaining_qty !== null
      ? Math.max(0, num(line.remaining_qty))
      : Math.max(0, lineOrdered - lineDelivered);
    const price = num(line.unit_price);
    ordered += lineOrdered;
    delivered += Math.min(lineDelivered, lineOrdered);
    remaining += lineRemaining;
    linesValue += lineOrdered * price;
    remainingValue += lineRemaining * price;
    if (lineRemaining > 0) openLines += 1;
  });

  const status = text(lpo?.status);
  const cancelled = lower(status) === 'cancelled';
  const closed = CLOSED_STATUSES.has(lower(status));
  const fullyDelivered = lines.length > 0 && remaining <= 0;
  let deliveryState = 'Not delivered';
  if (cancelled) deliveryState = 'Cancelled';
  else if (fullyDelivered) deliveryState = 'Delivered';
  else if (closed) deliveryState = status;
  else if (delivered > 0) deliveryState = 'Partially delivered';

  const isOpen = !closed && !fullyDelivered;
  const expected = text(lpo?.expected_start_delivery_date);
  const days = expected ? ageDays(expected, { now }) : null;
  const daysLate = isOpen && remaining > 0 && days !== null && days > 0 ? days : 0;
  const projectName = text(lpo?.project_name);

  return {
    lpo,
    id: lpo?.id,
    number: text(lpo?.lpo_number),
    supplier: text(lpo?.supplier),
    projectId: text(lpo?.project_id),
    projectName,
    projectKey: projectName ? text(lpo?.project_id) : NONE,
    supplierKey: text(lpo?.supplier) || NONE,
    value: num(lpo?.total_value),
    currency: text(lpo?.currency) || 'AED',
    lines,
    lineCount: lines.length,
    openLines,
    ordered,
    delivered,
    remaining,
    linesValue,
    remainingValue,
    deliveredPct: ordered > 0 ? (delivered / ordered) * 100 : 0,
    deliveryState,
    isOpen,
    expected,
    daysLate,
    isLate: daysLate > 0,
  };
}

/** Most late first, then the soonest expected date, then LPO number. */
export function compareLpos(a, b) {
  if (a.daysLate !== b.daysLate) return b.daysLate - a.daysLate;
  if (a.expected !== b.expected) {
    if (!a.expected) return 1;
    if (!b.expected) return -1;
    return a.expected < b.expected ? -1 : 1;
  }
  return a.number.localeCompare(b.number, undefined, { numeric: true });
}

/** Status filter options in the order of the delivery lifecycle. */
export const LPO_STATUS_OPTIONS = ['Late', 'Not delivered', 'Partially delivered', 'Delivered', 'Closed', 'Cancelled'];

export function filterLpos(summaries, { view = 'open', search = '', project = null, supplier = null, status = null } = {}) {
  const needle = lower(search);
  return summaries.filter((item) => {
    if (view === 'open' && !item.isOpen) return false;
    if (project && item.projectKey !== project) return false;
    if (supplier && item.supplierKey !== supplier) return false;
    if (status === 'Late' && !item.isLate) return false;
    if (status && status !== 'Late' && item.deliveryState !== status) return false;
    if (!needle) return true;
    const haystack = [
      item.number, item.supplier, item.projectName,
      ...item.lines.flatMap((line) => [line.part_number, line.description]),
    ].map(lower);
    return haystack.some((value) => value.includes(needle));
  });
}

/* ------------------------------------------------------------------ site requests */

/** Open site requests (still something to deliver), soonest due first, with their urgency. */
export function normalizeSiteRequests(requests, { now } = {}) {
  return (Array.isArray(requests) ? requests : [])
    .filter((request) => num(request.remaining_qty) > 0)
    .map((request) => {
      const due = text(request.due_date);
      const dueDays = due ? ageDays(due, { now }) : null; // > 0 = past due
      let urgency = 'later';
      if (dueDays !== null && dueDays > 0) urgency = 'overdue';
      else if (dueDays !== null && dueDays >= -7) urgency = 'this_week';
      return { ...request, dueDays, urgency, remaining: num(request.remaining_qty), unit: text(request.unit) || 'pcs' };
    })
    .sort((a, b) => {
      if (!a.due_date && b.due_date) return 1;
      if (a.due_date && !b.due_date) return -1;
      if (a.due_date !== b.due_date) return a.due_date < b.due_date ? -1 : 1;
      return text(a.part_number).localeCompare(text(b.part_number));
    });
}

/* ------------------------------------------------------------------ KPIs */

/**
 * The four KPI tiles + header attention counts.
 * Open orders = value of LPOs still open; Late = open LPOs past their expected date with qty left;
 * Delivered = delivered / ordered pieces over open LPOs; Site requests = open requests.
 */
export function procurementKpis(summaries, siteRequests) {
  const open = summaries.filter((item) => item.isOpen).sort(compareLpos);
  const late = open.filter((item) => item.isLate);
  const suppliers = [...new Set(open.map((item) => item.supplier).filter(Boolean))];
  const ordered = open.reduce((sum, item) => sum + item.ordered, 0);
  const delivered = open.reduce((sum, item) => sum + item.delivered, 0);
  const overdue = siteRequests.filter((request) => request.urgency === 'overdue').length;
  const thisWeek = siteRequests.filter((request) => request.urgency === 'this_week').length;
  return {
    openCount: open.length,
    openValue: open.reduce((sum, item) => sum + item.value, 0),
    suppliers,
    lateCount: late.length,
    lateLines: late.reduce((sum, item) => sum + item.openLines, 0),
    oldestLate: late[0] || null,
    ordered,
    delivered,
    deliveredPct: ordered > 0 ? (delivered / ordered) * 100 : 0,
    siteRequestCount: siteRequests.length,
    siteRequestsOverdue: overdue,
    siteRequestsThisWeek: thisWeek,
  };
}

/* ------------------------------------------------------------------ export */

/** One CSV row per LPO line (an LPO without lines still gets one row). */
export function lpoExportRows(summaries) {
  return summaries.flatMap((item) => {
    const base = {
      lpo: item.number,
      supplier: item.supplier,
      project: item.projectName,
      status: item.deliveryState,
      expected: item.expected,
      days_late: item.daysLate || '',
      lpo_value: item.value,
    };
    if (!item.lines.length) return [{ ...base }];
    return item.lines.map((line) => ({
      ...base,
      part_number: line.part_number,
      description: line.description,
      ordered: num(line.ordered_qty),
      delivered: num(line.delivered_qty),
      remaining: line.remaining_qty ?? Math.max(0, num(line.ordered_qty) - num(line.delivered_qty)),
      unit_price: num(line.unit_price),
    }));
  });
}

export const LPO_EXPORT_COLUMNS = [
  { key: 'lpo', label: 'LPO' },
  { key: 'supplier', label: 'Supplier' },
  { key: 'project', label: 'Project' },
  { key: 'status', label: 'Status' },
  { key: 'expected', label: 'Expected delivery' },
  { key: 'days_late', label: 'Days late' },
  { key: 'lpo_value', label: 'LPO value (AED)' },
  { key: 'part_number', label: 'Part number' },
  { key: 'description', label: 'Description' },
  { key: 'ordered', label: 'Ordered' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'remaining', label: 'Remaining' },
  { key: 'unit_price', label: 'Unit price (AED)' },
];

export const DN_EXPORT_COLUMNS = [
  { key: 'dn_number', label: 'DN' },
  { key: 'delivery_date', label: 'Delivery date' },
  { key: 'project_name', label: 'Project' },
  { key: 'supplier', label: 'Supplier' },
  { key: 'lpo_number', label: 'LPO' },
  { key: 'destination', label: 'Destination', value: (dn) => (dn.delivery_type === 'to_site' ? 'Direct to site' : 'Warehouse') },
  { key: 'store', label: 'Store', value: (dn) => (dn.delivery_type === 'to_site' ? '' : (dn.destination_location || '')) },
  { key: 'items', label: 'Items', value: (dn) => (Array.isArray(dn.line_items) ? dn.line_items.length : 0) },
  { key: 'received_by', label: 'Received by' },
];

export const SITE_REQUEST_EXPORT_COLUMNS = [
  { key: 'due_date', label: 'Due date' },
  { key: 'project_name', label: 'Project' },
  { key: 'part_number', label: 'Part number' },
  { key: 'description', label: 'Description' },
  { key: 'remaining', label: 'Qty still needed' },
  { key: 'unit', label: 'Unit' },
  { key: 'notes', label: 'Notes' },
];

/* ------------------------------------------------------------------ wave 2: table, detail, forms */

/** "Sort: …" options of the Open orders table. 'late' = most late first (compareLpos). */
export const LPO_SORTS = [
  { key: 'late', label: 'Most late' },
  { key: 'expected', label: 'Expected date' },
  { key: 'value', label: 'Value' },
  { key: 'number', label: 'LPO number' },
];

export function sortLpos(summaries, sortKey = 'late') {
  const list = [...summaries];
  if (sortKey === 'expected') {
    return list.sort((a, b) => {
      if (a.expected === b.expected) return compareLpos(a, b);
      if (!a.expected) return 1;
      if (!b.expected) return -1;
      return a.expected < b.expected ? -1 : 1;
    });
  }
  if (sortKey === 'value') return list.sort((a, b) => (b.value - a.value) || compareLpos(a, b));
  if (sortKey === 'number') return list.sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }));
  return list.sort(compareLpos);
}

/**
 * Do the LPO's lines add up to its value? Differences of 1 AED or less are rounding.
 * → { ok, linesValue, value, difference } (ok is true when there are no priced lines to compare).
 */
export function lpoValueCheck(summary) {
  const priced = summary.lines.some((line) => num(line.unit_price) > 0);
  const difference = summary.linesValue - summary.value;
  return { ok: !priced || Math.abs(difference) <= 1, priced, linesValue: summary.linesValue, value: summary.value, difference };
}

/** The value still to come on an LPO (remaining × unit price), and the pieces. */
export const toCome = (summary) => ({ pcs: summary.remaining, value: summary.remainingValue });

/** Site requests in urgency groups (empty groups left out): Overdue · Due this week · Later. */
export const URGENCY_GROUPS = [
  { key: 'overdue', label: 'Overdue', tone: 'red' },
  { key: 'this_week', label: 'Due this week', tone: 'amber' },
  { key: 'later', label: 'Later', tone: null },
];

export function groupSiteRequests(requests) {
  return URGENCY_GROUPS
    .map((group) => ({ ...group, items: requests.filter((request) => request.urgency === group.key) }))
    .filter((group) => group.items.length);
}

/**
 * Lines a "Receive delivery" form starts with for an LPO: every line still to come, qty = what remains.
 * (Fully delivered lines are left out; the form says how many.)
 */
export function receiveLinesFor(lpo) {
  const lines = Array.isArray(lpo?.line_items) ? lpo.line_items : [];
  return lines
    .map((line, index) => {
      const ordered = num(line.ordered_qty);
      const delivered = num(line.delivered_qty);
      const remaining = line.remaining_qty !== undefined && line.remaining_qty !== null
        ? Math.max(0, num(line.remaining_qty))
        : Math.max(0, ordered - delivered);
      return {
        key: line.id || `${line.part_number}-${index}`,
        part_number: text(line.part_number),
        description: text(line.description),
        ordered,
        delivered,
        remaining,
        qty: remaining,
        fromLpo: true,
      };
    })
    .filter((line) => line.part_number && line.remaining > 0);
}

/** Total of an LPO form's lines (qty × unit price), ignoring unfinished rows. */
export function lpoLinesTotal(lines) {
  return (Array.isArray(lines) ? lines : []).reduce((sum, line) => {
    const qty = toNumber(line.ordered_qty);
    const price = toNumber(line.unit_price);
    return qty > 0 && price >= 0 && qty !== null && price !== null ? sum + qty * price : sum;
  }, 0);
}

/** Rounds money to fils so 0.1 + 0.2 never shows as a difference. */
export const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;
